import {
  addDays,
  compareCalendarTasks,
  daysBetween,
  type Entity,
  type Occurrence,
} from "@kkindex/shared";

export type CalendarBar = {
  item: Entity;
  start: string;
  end: string;
  event?: Occurrence;
};
// Reserve each task's row for its entire date range, including empty cells above it.
// Saved day orders express precedence; newer choices win when older ones conflict.
export function calendarLanes(bars: CalendarBar[]) {
  const tasks = bars.filter((bar) => !bar.event);
  const byId = new Map(tasks.map((bar) => [bar.item.id, bar]));
  const after = new Map(tasks.map((bar) => [bar.item.id, new Set<string>()]));
  const before = new Map(tasks.map((bar) => [bar.item.id, new Set<string>()]));
  const hints = new Map<string, number>();
  for (const bar of tasks)
    for (const date of Object.keys(bar.item.calendarOrder))
      if (date >= bar.start && date <= bar.end)
        hints.set(
          date,
          Math.max(
            hints.get(date) ?? 0,
            bar.item.calendarOrderUpdatedAt[date] ?? 0,
          ),
        );
  function reaches(from: string, to: string) {
    const pending = [from],
      seen = new Set<string>();
    while (pending.length) {
      const id = pending.pop()!;
      if (id === to) return true;
      if (seen.has(id)) continue;
      seen.add(id);
      pending.push(...after.get(id)!);
    }
    return false;
  }
  for (const [date] of [...hints].sort(
    (a, b) => b[1] - a[1] || b[0].localeCompare(a[0]),
  )) {
    const ordered = tasks
      .filter((bar) => bar.start <= date && bar.end >= date)
      .sort((a, b) => compareCalendarTasks(a.item, b.item, date));
    for (let i = 1; i < ordered.length; i++) {
      const upper = ordered[i - 1].item.id,
        lower = ordered[i].item.id;
      if (reaches(lower, upper)) continue;
      after.get(upper)!.add(lower);
      before.get(lower)!.add(upper);
    }
  }
  const lanes = new Map<string, number>();
  const rows: CalendarBar[][] = [];
  const remaining = new Set(byId.keys());
  const defaults = [...tasks].sort(
    (a, b) =>
      a.start.localeCompare(b.start) ||
      b.end.localeCompare(a.end) ||
      a.item.order - b.item.order ||
      a.item.id.localeCompare(b.item.id),
  );
  while (remaining.size) {
    const bar = defaults.find(
      (bar) =>
        remaining.has(bar.item.id) &&
        [...before.get(bar.item.id)!].every((id) => lanes.has(id)),
    )!;
    let lane = Math.max(
      0,
      ...[...before.get(bar.item.id)!].map((id) => lanes.get(id)!),
    );
    while (
      rows[lane]?.some(
        (other) => other.start <= bar.end && other.end >= bar.start,
      )
    )
      lane++;
    (rows[lane] ??= []).push(bar);
    lanes.set(bar.item.id, lane + 1);
    remaining.delete(bar.item.id);
  }
  return lanes;
}

export function calendarDay(
  bars: CalendarBar[],
  date: string,
  lanes = calendarLanes(bars),
) {
  return bars
    .filter((b) => b.start <= date && b.end >= date)
    .sort((a, b) => {
      if (!!a.event !== !!b.event) return a.event ? -1 : 1;
      if (!a.event) return lanes.get(a.item.id)! - lanes.get(b.item.id)!;
      return (
        a.start.localeCompare(b.start) ||
        b.end.localeCompare(a.end) ||
        a.item.order - b.item.order ||
        a.item.id.localeCompare(b.item.id)
      );
    });
}

export function calendarWeek(
  bars: CalendarBar[],
  week: string,
  lanes = calendarLanes(bars),
) {
  const segments: {
    bar: CalendarBar;
    first: number;
    last: number;
    lane: number;
  }[] = [];
  for (const bar of bars.filter(
    (bar) => !bar.event && bar.start <= addDays(week, 6) && bar.end >= week,
  )) {
    const lane = lanes.get(bar.item.id)!;
    if (lane <= 2)
      segments.push({
        bar,
        first: Math.max(0, daysBetween(week, bar.start)),
        last: Math.min(6, daysBetween(week, bar.end)),
        lane,
      });
  }
  const previous = new Map<string, (typeof segments)[number]>();
  const overflow: { date: string; tasks: number; events: number }[] = [];
  for (let day = 0; day < 7; day++) {
    const date = addDays(week, day);
    const list = calendarDay(bars, date, lanes);
    const events = list.filter((b) => b.event);
    const tasks = list.filter((b) => !b.event);
    const shown = [...events.slice(0, 1).map((bar) => ({ bar, lane: 0 }))];
    for (const { bar, lane } of shown) {
      const key = `${bar.event?.id || bar.item.id}:${lane}`;
      const last = previous.get(key);
      if (last && last.last === day - 1) last.last = day;
      else {
        const segment = { bar, first: day, last: day, lane };
        segments.push(segment);
        previous.set(key, segment);
      }
    }
    overflow.push({
      date,
      tasks: tasks.filter((bar) => lanes.get(bar.item.id)! > 2).length,
      events: Math.max(0, events.length - 1),
    });
  }
  return { segments, overflow };
}
